import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  useShadowRoot,
  createTechDocsAddonExtension,
  TechDocsAddonLocations,
} from '@backstage/plugin-techdocs-react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';

import { oneadvancedPlugin } from '../../plugin';

// Inline SVG for the expand icon (renders inside shadow DOM where MUI styles aren't available)
const EXPAND_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="currentColor">' +
  '<path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>' +
  '</svg>';

function attachExpandButton(
  el: HTMLElement,
  onExpand: (svgHtml: string) => void,
  paletteMode: 'light' | 'dark',
): () => void {
  el.style.position = 'relative';

  const btn = document.createElement('button');
  btn.className = 'oa-mermaid-expand-btn';
  btn.setAttribute('aria-label', 'Expand diagram');
  btn.title = 'Expand diagram';
  btn.innerHTML = EXPAND_ICON;

  const isDark = paletteMode === 'dark';
  Object.assign(btn.style, {
    position: 'absolute',
    top: '6px',
    right: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    padding: '0',
    // Both modes use a white/light button — on light diagrams it blends in
    // subtly; on dark backgrounds it stands out clearly.
    background: isDark ? 'rgba(255,255,255,0.88)' : 'rgba(255,255,255,0.80)',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.15)'}`,
    borderRadius: '4px',
    cursor: 'pointer',
    color: isDark ? 'rgba(0,0,0,0.85)' : 'rgba(0,0,0,0.5)',
    // Faintly visible by default so users discover it without needing to hover
    opacity: '0.4',
    transition: 'opacity 0.2s, box-shadow 0.2s',
    zIndex: '10',
  });

  const onEnter = () => {
    btn.style.opacity = '1';
    btn.style.boxShadow = '0 2px 6px rgba(0,0,0,0.25)';
  };
  const onLeave = () => {
    btn.style.opacity = '0.4';
    btn.style.boxShadow = 'none';
  };
  const onClick = (e: Event) => {
    e.stopPropagation();
    const svgEl = el.querySelector('svg');
    if (svgEl) {
      // Strip fixed pixel dimensions AND max-width from the inline style that
      // mermaid bakes in (e.g. style="max-width: 500px;"). Without this the SVG
      // refuses to grow beyond its original render size regardless of container width.
      const responsive = svgEl.outerHTML
        .replace(/\s+width="[^"]*"/, '')
        .replace(/\s+height="[^"]*"/, '')
        .replace(/(<svg\b[^>]*)\sstyle="([^"]*)"/, (_m, tag, style) => {
          const cleaned = style
            .split(';')
            .map((s: string) => s.trim())
            .filter((s: string) => s && !/^max-width\s*:/.test(s))
            .join('; ');
          return cleaned ? `${tag} style="${cleaned}"` : tag;
        });
      onExpand(responsive);
    }
  };

  el.addEventListener('mouseenter', onEnter);
  el.addEventListener('mouseleave', onLeave);
  btn.addEventListener('click', onClick);
  el.appendChild(btn);

  return () => {
    el.removeEventListener('mouseenter', onEnter);
    el.removeEventListener('mouseleave', onLeave);
    btn.removeEventListener('click', onClick);
    if (btn.parentNode) btn.remove();
  };
}

const ZOOM_STEP = 0.1;
const ZOOM_MIN = 0.1;
const ZOOM_MAX = 4;

function MermaidExpandAddonComponent() {
  const [expandedSvg, setExpandedSvg] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const shadowRoot = useShadowRoot();
  const { mode: paletteMode } = useTheme().palette;

  // Ref for the scrollable dialog content area
  const contentRef = useRef<HTMLDivElement | null>(null);
  // Stores the scroll centre fraction captured just before a zoom change
  const scrollCentreRef = useRef({ x: 0.5, y: 0.5 });

  // Single entry-point for all zoom changes: captures the current scroll
  // centre fraction so useLayoutEffect can restore it after the DOM updates.
  const changeZoom = useCallback((updater: number | ((z: number) => number)) => {
    const el = contentRef.current;
    if (el) {
      scrollCentreRef.current = {
        x: el.scrollWidth > el.clientWidth
          ? (el.scrollLeft + el.clientWidth / 2) / el.scrollWidth
          : 0.5,
        y: el.scrollHeight > el.clientHeight
          ? (el.scrollTop + el.clientHeight / 2) / el.scrollHeight
          : 0.5,
      };
    }
    setZoom(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      // Round to 1 dp to avoid floating-point drift (e.g. 0.30000000000000004)
      return Math.round(Math.min(Math.max(next, ZOOM_MIN), ZOOM_MAX) * 10) / 10;
    });
  }, []);

  // After each zoom commit, restore the scroll centre. useLayoutEffect fires
  // after React has updated the DOM but before the browser paints, so
  // scrollWidth already reflects the new width with no visual flash.
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const { x, y } = scrollCentreRef.current;
    el.scrollLeft = x * el.scrollWidth - el.clientWidth / 2;
    el.scrollTop = y * el.scrollHeight - el.clientHeight / 2;
  }, [zoom]);

  // Ctrl+scroll to zoom. Attached to document (not the content div) so
  // preventDefault() fires before the browser acts on Ctrl+wheel to page-zoom.
  // The listener is only registered while the dialog is open.
  useEffect(() => {
    if (!expandedSvg) return undefined;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      changeZoom(z => z + (e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP));
    };
    document.addEventListener('wheel', onWheel, { passive: false });
    return () => document.removeEventListener('wheel', onWheel);
  }, [expandedSvg, changeZoom]);

  // Stable ref so the MutationObserver callback never holds a stale setter
  const onExpandRef = useRef<(svgHtml: string) => void>(null!);
  onExpandRef.current = (svgHtml: string) => {
    setExpandedSvg(svgHtml);
    setZoom(1);
  };

  useEffect(() => {
    if (!shadowRoot) return undefined;

    // Map keeps one cleanup function per processed element so we never
    // double-attach and can cleanly remove on unmount / page navigation.
    const cleanups = new Map<HTMLElement, () => void>();

    const processAll = () => {
      shadowRoot.querySelectorAll<HTMLElement>('.mermaid').forEach(el => {
        // Skip elements already handled or still awaiting SVG render
        if (cleanups.has(el)) return;
        if (!el.querySelector('svg')) return;

        const cleanup = attachExpandButton(el, svgHtml =>
          onExpandRef.current(svgHtml),
          paletteMode,
        );
        cleanups.set(el, cleanup);
      });
    };

    // Watch for any DOM change in the shadow root (mermaid renders async)
    const observer = new MutationObserver(processAll);
    observer.observe(shadowRoot, { childList: true, subtree: true });
    processAll(); // handle diagrams already present

    return () => {
      observer.disconnect();
      cleanups.forEach(fn => fn());
      cleanups.clear();
    };
  }, [shadowRoot, paletteMode]);

  const handleClose = () => {
    setExpandedSvg(null);
    setZoom(1);
    scrollCentreRef.current = { x: 0.5, y: 0.5 };
  };

  if (expandedSvg === null) return null;

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        style: {
          width: '92vw',
          maxWidth: '92vw',
          height: '88vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: 1,
          px: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="h6" component="span" sx={{ mr: 1 }}>
            Diagram
          </Typography>
          <Tooltip title="Zoom in (+10%)">
            <IconButton
              size="small"
              onClick={() => changeZoom(z => z + ZOOM_STEP)}
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset zoom">
            <IconButton size="small" onClick={() => changeZoom(1)}>
              <ZoomOutMapIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom out (−10%)">
            <IconButton
              size="small"
              onClick={() => changeZoom(z => z - ZOOM_STEP)}
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5, mr: 2 }}>
            {Math.round(zoom * 100)}%
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Ctrl+scroll to zoom
          </Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent
        ref={contentRef}
        sx={{
          overflow: 'auto',
          p: 3,
        }}
      >
        {/* Width-based zoom: changes layout footprint so scrollbars appear correctly.
            No CSS transition — the instant layout change lets useLayoutEffect read
            the correct scrollWidth before the browser paints. */}
        <Box
          sx={{
            width: `${zoom * 100}%`,
            minWidth: `${zoom * 100}%`,
            lineHeight: 0,
            // !important needed to beat any residual inline styles mermaid left on the SVG
            '& svg': {
              width: '100% !important',
              height: 'auto !important',
              maxWidth: 'none !important',
              display: 'block',
            },
          }}
          // The SVG comes from the TechDocs shadow DOM content which is already
          // sanitised by the mermaid renderer — same trust level as dangerouslySetInnerHTML
          // used by the shadow DOM itself.
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: expandedSvg }}
        />
      </DialogContent>
    </Dialog>
  );
}

export const MermaidExpand = oneadvancedPlugin.provide(
  createTechDocsAddonExtension({
    name: 'MermaidExpand',
    location: TechDocsAddonLocations.Content,
    component: MermaidExpandAddonComponent,
  }),
);
